import React, { useState } from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse,
  Box, Typography, Divider, Chip, IconButton, Tooltip,
} from '@mui/material';
import {
  Dashboard, Speed, Assessment, Phone, ExpandLess, ExpandMore, Call, SwapHoriz,
  AutoAwesome, Storage, DarkMode, LightMode,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useThemeMode } from '../../hooks/useThemeMode';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  currentSection: string;
  onSectionChange: (section: string) => void;
}

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  badge?: string;
  children?: NavigationItem[];
}

const SIDEBAR_WIDTH = 260;

const badgeColor = (badge: string): { bg: string; color: string; border: string } => {
  switch (badge) {
    case 'AUTO': return { bg: 'rgba(236,72,153,0.12)', color: '#F472B6', border: 'rgba(236,72,153,0.2)' };
    case 'ADMIN': return { bg: 'rgba(245,158,11,0.12)', color: '#FBBF24', border: 'rgba(245,158,11,0.24)' };
    case 'DID': return { bg: 'rgba(59,130,246,0.12)', color: '#60A5FA', border: 'rgba(59,130,246,0.2)' };
    case 'CODE': return { bg: 'rgba(6,182,212,0.12)', color: '#22D3EE', border: 'rgba(6,182,212,0.2)' };
    default: return { bg: 'rgba(99,102,241,0.12)', color: '#818CF8', border: 'rgba(99,102,241,0.2)' };
  }
};

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, currentSection, onSectionChange }) => {
  const [expandedItems, setExpandedItems] = useState<string[]>(['reports']);
  const theme = useTheme();
  const { isDark, toggleMode } = useThemeMode();

  const authUserStr = localStorage.getItem('auth_user');
  let currentUser = null;
  if (authUserStr) {
    try { currentUser = JSON.parse(authUserStr).username; } catch (e) {}
  }
  const isSmartClinicUser = currentUser === 'smartclinic';
  const isAdminUser = currentUser === 'admin';

  const handleToggleExpand = (itemId: string) => {
    setExpandedItems(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  };

  const navigationItems: NavigationItem[] = [
    { id: 'overview', label: 'Overview', icon: <Dashboard />, badge: 'NEW' },
    { id: 'overview2', label: 'Overview V2', icon: <Dashboard />, badge: 'NEW' },
    { id: 'callback-advanced', label: 'CallBack Advanced', icon: <Call /> },
    { id: 'called-numbers', label: 'Numeri Chiamati', icon: <Phone /> },
    { id: 'called-numbers-v2', label: 'Numeri Chiamati V2', icon: <Phone />, badge: 'NEW' },
    { id: 'inoltri-struttura', label: 'Inoltri in Struttura', icon: <SwapHoriz />, badge: 'NEW' },
    ...(isAdminUser ? [{ id: 'cdr', label: 'CDR Live', icon: <Storage />, badge: 'ADMIN' }] : []),
    ...(!isSmartClinicUser ? [{ id: 'automated-recall', label: 'Recall Automatico V2', icon: <AutoAwesome />, badge: 'AUTO' }] : []),
    {
      id: 'reports', label: 'Reports', icon: <Assessment />,
      children: [
        { id: 'smartclinic-report', label: 'Report SmartClinic', icon: <Assessment /> },
        { id: 'smartclinic-ftr-report', label: 'SmartClinic FTR', icon: <Assessment />, badge: 'DID' },
        { id: 'smartclinic-ftr-v2-report', label: 'SmartClinic FTR V2', icon: <Assessment />, badge: 'CODE' },
        ...(!isSmartClinicUser ? [{ id: 'smartclinic-did', label: 'SmartClinic DID', icon: <Assessment />, badge: 'NEW' }] : []),
        { id: 'custom-reports', label: 'Custom Reports', icon: <Assessment /> },
        { id: 'scheduled-reports', label: 'Scheduled Reports', icon: <Assessment /> },
      ],
    },
  ];

  const renderNavigationItem = (item: NavigationItem, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isSelected = currentSection === item.id;
    const bc = item.badge ? badgeColor(item.badge) : null;

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ px: 1, py: 0.25 }}>
          <ListItemButton
            selected={isSelected}
            onClick={() => {
              if (hasChildren) handleToggleExpand(item.id);
              else { onSectionChange(item.id); if (window.innerWidth < 900) onClose(); }
            }}
            sx={{
              borderRadius: 2, pl: 2 + depth * 2, pr: 1.5, py: 0.75, position: 'relative',
              transition: 'all 150ms ease',
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                '&::before': {
                  content: '""', position: 'absolute', left: 0, top: '20%', bottom: '20%', width: 3,
                  borderRadius: '0 4px 4px 0', background: 'linear-gradient(180deg, #6366F1, #8B5CF6)',
                },
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) },
              },
              '&:hover': { backgroundColor: isDark ? 'rgba(148,163,184,0.04)' : 'rgba(15,23,42,0.04)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: isSelected ? theme.palette.primary.light : theme.palette.text.secondary, '& .MuiSvgIcon-root': { fontSize: 20 } }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{
              fontSize: depth > 0 ? '0.8125rem' : '0.875rem',
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? theme.palette.text.primary : theme.palette.text.secondary,
            }} />
            {item.badge && bc && (
              <Chip label={item.badge} size="small" sx={{
                fontSize: '0.625rem', fontWeight: 600, height: 20, borderRadius: 1.5,
                backgroundColor: bc.bg, color: bc.color, border: `1px solid ${bc.border}`, '& .MuiChip-label': { px: 1 },
              }} />
            )}
            {hasChildren && (isExpanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />)}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>{item.children!.map(child => renderNavigationItem(child, depth + 1))}</List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box sx={{ width: SIDEBAR_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Speed sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1, color: 'text.primary' }}>BiSolution</Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, background: 'linear-gradient(135deg, #6366F1, #06B6D4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', lineHeight: 1 }}>Analytics</Typography>
        </Box>
      </Box>
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }} className="custom-scroll">
        <List sx={{ pt: 0 }}>{navigationItems.map(item => renderNavigationItem(item))}</List>
      </Box>
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">v1.0.0</Typography>
        <Tooltip title={isDark ? 'Passa a Light Mode' : 'Passa a Dark Mode'}>
          <IconButton size="small" onClick={toggleMode} sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
            {isDark ? <LightMode sx={{ fontSize: 18 }} /> : <DarkMode sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer variant="persistent" anchor="left" open={open}
        sx={{ display: { xs: 'none', md: 'block' }, width: SIDEBAR_WIDTH, flexShrink: 0,
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, boxSizing: 'border-box', position: 'relative', height: '100vh', overflowY: 'auto' } }}>
        {drawerContent}
      </Drawer>
      <Drawer variant="temporary" anchor="left" open={open} onClose={onClose} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, boxSizing: 'border-box' } }}>
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
